import { Client } from '@stomp/stompjs';
import { API_URL, getToken } from '../variables/constants';

/**
 * Connexion STOMP unique pour toute l'application.
 *
 * AUTHENTIFICATION PAR LA FRAME CONNECT. Le navigateur ne peut pas poser d'en-tête
 * sur le handshake WebSocket, et mettre le jeton dans l'URL le ferait apparaître
 * dans les journaux du proxy. Le JWT part donc dans `connectHeaders`, validé côté
 * serveur par `WsAuthChannelInterceptor`.
 *
 * RECONNEXION À BACKOFF EXPONENTIEL, plafonnée à 30 s. On ne tape jamais le serveur
 * en boucle : chaque échec double le délai. Le jeton est relu à chaque tentative,
 * un rafraîchissement de session pendant la coupure est donc pris en compte.
 *
 * MAIS le backoff protège un serveur en panne, pas l'utilisateur : dès que le
 * réseau revient (`online`) ou que l'onglet reprend la main (`focus`,
 * `visibilitychange`), on retente IMMÉDIATEMENT. Un appel entrant vit 45 s -
 * attendre le prochain palier de 30 s, c'est le manquer.
 *
 * Les abonnements sont mémorisés ici : à la reconnexion ils sont rejoués sans que
 * les composants aient à le savoir. Les composants, eux, comblent le trou par
 * `GET /messages?depuis=…` quand `onReconnect` est appelé.
 */

const DELAI_MIN = 1000;
const DELAI_MAX = 30000;
/* Erreur STOMP qui n'est PAS un refus de jeton : delai court mais non nul, pour
   ne pas marteler un serveur qui rejette systematiquement un abonnement. */
const DELAI_ERREUR = 5000;

/** `http(s)://hote/api` → `ws(s)://hote/api/ws`. */
const urlWs = () => {
  const http = API_URL.replace(/\/+$/, '');
  return `${http.replace(/^http/, 'ws')}/ws`;
};

let client = null;
let delai = DELAI_MIN;
let etat = 'deconnecte'; // deconnecte | connexion | connecte
let aDejaEteConnecte = false;
const abonnements = new Map(); // cle -> { destination, callback, souscription }
const ecouteursEtat = new Set();
const ecouteursReconnexion = new Set();

const notifierEtat = () => ecouteursEtat.forEach((f) => { try { f(etat); } catch { /* écouteur défaillant */ } });

const creer = () => {
  const c = new Client({
    brokerURL: urlWs(),
    reconnectDelay: 0, // on pilote nous-mêmes le backoff
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    beforeConnect: () => {
      const token = getToken();
      c.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    },
    onConnect: () => {
      etat = 'connecte';
      delai = DELAI_MIN;
      abonnements.forEach((a) => {
        a.souscription = c.subscribe(a.destination, (frame) => {
          try { a.callback(JSON.parse(frame.body)); } catch { /* corps non JSON */ }
        });
      });
      notifierEtat();
      if (aDejaEteConnecte) ecouteursReconnexion.forEach((f) => { try { f(); } catch { /* ignore */ } });
      aDejaEteConnecte = true;
    },
    onWebSocketClose: () => {
      if (etat !== 'deconnecte') {
        etat = 'connexion';
        notifierEtat();
      }
      abonnements.forEach((a) => { a.souscription = null; });
      planifierReconnexion();
    },
    onStompError: (frame) => {
      // Ne pas confondre deux choses que le broker signale de la meme facon.
      // Un JETON refuse ne se resoudra pas tout seul : on attend le plafond.
      // Un ABONNEMENT rejete - « vous n'etes pas membre de cette conversation »
      // - ferme aussi la connexion, et punir l'utilisateur de 30 s sans
      // notification ni appel entrant pour cela serait absurde.
      const message = (frame?.headers?.message || '').toLowerCase();
      delai = message.includes('jeton') ? DELAI_MAX : DELAI_ERREUR;
    },
  });
  return c;
};

let minuterie = null;
const planifierReconnexion = () => {
  if (minuterie || !client) return;
  minuterie = setTimeout(() => {
    minuterie = null;
    if (!client || !getToken()) return;
    delai = Math.min(delai * 2, DELAI_MAX);
    client.activate();
  }, delai);
};

/**
 * Reconnexion immediate.
 *
 * Le backoff exponentiel protege un serveur en panne ; il n'a aucune raison de
 * faire patienter 30 s un utilisateur dont le reseau vient de revenir ou qui
 * reprend la main sur son onglet. Un appel entrant a une duree de vie de 45 s :
 * attendre le prochain palier, c'est le manquer.
 */
const reconnecterMaintenant = () => {
  if (!client || etat === 'connecte' || !getToken()) return;
  if (minuterie) { clearTimeout(minuterie); minuterie = null; }
  delai = DELAI_MIN;
  try { client.activate(); } catch { /* activation deja en cours */ }
};

/* Poses une seule fois, au chargement du module : `demarrer()` peut etre appele
   par plusieurs composants, et `arreter()` remet `client` a null. */
if (typeof window !== 'undefined') {
  window.addEventListener('online', reconnecterMaintenant);
  window.addEventListener('focus', reconnecterMaintenant);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) reconnecterMaintenant();
  });
}

const stompClient = {
  /** Idempotent : la première page qui en a besoin ouvre la connexion. */
  demarrer() {
    if (client) return;
    if (!getToken()) return;
    client = creer();
    etat = 'connexion';
    notifierEtat();
    client.activate();
  },

  arreter() {
    if (minuterie) { clearTimeout(minuterie); minuterie = null; }
    abonnements.clear();
    etat = 'deconnecte';
    notifierEtat();
    if (client) { client.deactivate(); client = null; }
    aDejaEteConnecte = false;
  },

  etat: () => etat,

  /** Force une tentative immediate - bouton « Reessayer » d'un bandeau de coupure. */
  reconnecter: reconnecterMaintenant,

  /** @returns fonction de désabonnement. */
  souscrire(destination, callback) {
    const cle = `${destination}#${Math.random().toString(36).slice(2)}`;
    const a = { destination, callback, souscription: null };
    abonnements.set(cle, a);
    if (client?.connected) {
      a.souscription = client.subscribe(destination, (frame) => {
        try { callback(JSON.parse(frame.body)); } catch { /* corps non JSON */ }
      });
    }
    return () => {
      abonnements.delete(cle);
      try { a.souscription?.unsubscribe(); } catch { /* déjà fermé */ }
    };
  },

  publier(destination, corps) {
    if (!client?.connected) return false;
    client.publish({ destination, body: JSON.stringify(corps) });
    return true;
  },

  surEtat(f) { ecouteursEtat.add(f); f(etat); return () => ecouteursEtat.delete(f); },
  surReconnexion(f) { ecouteursReconnexion.add(f); return () => ecouteursReconnexion.delete(f); },
};

export default stompClient;
